import React from 'react'
import { formatDateReplaceHour } from 'src/utils/formatDate'
import * as S from './FormAddNewBatches.styles'
import { Typography, IconButton } from '@mui/material'
import { useForm } from 'react-hook-form'
import { handleOnKeyDown } from 'src/utils/Form'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import { toast } from 'react-toastify'
import { NewBatchForEmptyGondola } from 'src/features/restocking/RestockingTypes'

interface FormAddNewBatchesProps {
  batches: NewBatchForEmptyGondola[]
  setBatches: React.Dispatch<React.SetStateAction<NewBatchForEmptyGondola[]>>
  quantityToCollect: number
  totalQuantity: number
  goBackAction: () => void
  minExpirationDate: Date
}

const FormAddNewBatches = ({
  batches,
  setBatches,
  quantityToCollect,
  totalQuantity,
  goBackAction,
  minExpirationDate,
}: FormAddNewBatchesProps) => {
  const {
    register, handleSubmit, reset, setFocus, formState: { errors },
  } = useForm<NewBatchForEmptyGondola>()

  const tableColumns = ['Validade', 'Quantidade', 'Remover']

  const onSubmit = (data: NewBatchForEmptyGondola) => {
    const quantity = Number(data.quantity)

    if (quantity + totalQuantity > quantityToCollect) {
      toast.error('Quantidade do lote supera o restante a separar!')
      return
    }

    setBatches([...batches, {
      quantity,
      expirationDate: data.expirationDate,
    }])

    reset()
    setFocus('quantity')
  }

  const removeBatch = (index: number) => {
    const updatedBatches = [...batches]
    updatedBatches.splice(index, 1)
    setBatches(updatedBatches)
  }

  return (
    <S.Container>
      <S.Form onSubmit={handleSubmit(onSubmit)}>
        <Typography sx={{ alignItems: 'center', fontSize: '17px' }} data-testid='minimum-expiration-date-info-test-id'>
          Validade mínima: <b>{formatDateReplaceHour(minExpirationDate)}</b>
        </Typography>
        <S.FormInputs>
          <S.CustomInputText
            inputProps={{
              ...register('quantity', {
                required: 'Campo obrigatório',
                min: { value: 1, message: 'Deve ser um valor positivo' },
                pattern: { value: new RegExp('^[0-9]*$'), message: 'Deve ser inteiro' },
              }),
              'data-testid': 'quantity-input-test-id',
            }}
            id='quantity'
            name='quantity'
            testId='quantity-input-test-id'
            label='Quantidade'
            type='number'
            autoComplete='off'
            onKeyDown={(e) => handleOnKeyDown(e, 'expirationDate')}
            error={!!errors.quantity}
            helperText={errors.quantity?.message}
            autoFocus
          />
          <S.CustomInputText
            inputProps={{
              ...register('expirationDate', {
                required: 'Campo obrigatório',
                validate: {
                  isValidDate: (value) => {
                    const date = new Date(value)
                    if (date < new Date(minExpirationDate)) {
                      return 'Abaixo da validade mínima'
                    }
                    return true
                  },
                },
              }),
              'data-testid': 'expiration-date-input-test-id',
              min: new Date(minExpirationDate).toISOString().split('T')[0],
            }}
            id='expirationDate'
            name='expirationDate'
            testId='expiration-date-input-test-id'
            label='Data de validade'
            type='date'
            autoComplete='off'
            error={!!errors.expirationDate}
            helperText={errors.expirationDate?.message}
            InputLabelProps={{ shrink: true }}
          />
        </S.FormInputs>
        <S.CustomButton
          sx={{ marginTop: '.7rem !important' }}
          type='submit'
          title='Adicionar Lote'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          data-testid='add-batch-button-test-id'
        />
        <S.ContainerQuantityInfo>
          <Typography sx={{ fontSize: '15px' }} data-testid='quantity-to-collect-info-test-id'>
            Separar: <b>{quantityToCollect}</b>
          </Typography>
          <Typography sx={{ fontSize: '15px' }} data-testid='total-quantity-info-test-id'>
            Separado: <b>{totalQuantity}</b>
          </Typography>
          <Typography sx={{ fontSize: '15px' }} data-testid='remaining-quantity-info-test-id'>
            Restante: <b>{quantityToCollect - totalQuantity}</b>
          </Typography>
        </S.ContainerQuantityInfo>
        <S.ContainerRows>
          <S.Row style={{
            position: 'sticky', top: 0, background: '#EFEFEF', zIndex: 1,
          }}
          >
            {tableColumns.map((column, index) => (
              <S.Column key={index}>
                <Typography sx={{ fontWeight: 'bold' }}>{column}</Typography>
              </S.Column>
            ))}
          </S.Row>
          {batches.length > 0 ? batches.map((batch, index) => ({ batch, index })).reverse().map(({ batch, index }) => (
            <S.Row
              key={index}
              data-testid='batch-table-row-test-id'
            >
              <S.Column>
                <Typography>{formatDateReplaceHour(new Date(batch.expirationDate))}</Typography>
              </S.Column>
              <S.Column>
                <Typography>{batch.quantity}</Typography>
              </S.Column>
              <S.Column>
                <IconButton
                  style={{
                    color: 'red',
                    cursor: 'pointer',
                  }}
                  onClick={() => removeBatch(index)}
                  data-testid='batch-table-remove-button-test-id'
                >
                  <DeleteOutline />
                </IconButton>
              </S.Column>
            </S.Row>
          )) : (
            <S.Row>
              <S.Column style={{ width: '100%', justifyContent: 'center' }}>
                <Typography>Nenhum lote adicionado</Typography>
              </S.Column>
            </S.Row>
          )}
        </S.ContainerRows>
      </S.Form>
      <S.ContainerButton>
        <S.CustomButton
          title='Voltar'
          bgColor='lightBlue3'
          hoverColor='darkBlueHover'
          letterColor='white'
          data-testid='add-batches-go-back-button-test-id'
          onClick={goBackAction}
        />
      </S.ContainerButton>
    </S.Container>
  )
}

export default FormAddNewBatches
